import { useState, useEffect, Suspense, lazy } from 'react';
import ChatFAB from './components/ChatFAB';
import RecaptchaProvider from './components/RecaptchaProvider';
import SectionTracker from './components/SectionTracker';
import Header from './sections/Header';
import Footer from './sections/Footer';
import ConsentBanner from './components/ConsentBanner';
import MetaPixel from './components/MetaPixel';
import Hero from './sections/Hero';
import { useScrollDepth } from './hooks/useSectionView';
import { useRoute } from './context/RouteContext';

// Lazy-load below-fold / off-path sections to cut initial JS competing with LCP.
// Hero is eager: it is the homepage LCP element (lazy Hero delayed paint in phase 2).
const ChatModal = lazy(() => import('./components/ChatModal'));
const AdminApp = lazy(() => import('./admin/AdminApp'));
const Assistant = lazy(() => import('./sections/Assistant'));
const Builders = lazy(() => import('./sections/Builders'));
const Contact = lazy(() => import('./sections/Contact'));
const Eldercare = lazy(() => import('./sections/Eldercare'));
const Fears = lazy(() => import('./sections/Fears'));
const EstimatorWizard = lazy(() => import('./sections/EstimatorWizard'));
const NotFound = lazy(() => import('./sections/NotFound'));
const PrivacyPolicy = lazy(() => import('./sections/PrivacyPolicy'));
const Proof = lazy(() => import('./sections/Proof'));
const RoomGuardian = lazy(() => import('./sections/RoomGuardian'));
const Rooms = lazy(() => import('./sections/Rooms'));
const TermsOfService = lazy(() => import('./sections/TermsOfService'));
const WhatHappens = lazy(() => import('./sections/WhatHappens'));
const FAQ = lazy(() => import('./sections/FAQ'));
const GuidesTeaser = lazy(() => import('./sections/GuidesTeaser'));
const GuideIndex = lazy(() => import('./sections/GuideIndex'));
const GuideArticle = lazy(() => import('./sections/GuideArticle'));

// Static import for findGuide since it's used in the route logic
import { findGuide } from './content/guides';

/**
 * Suspense fallback that reserves vertical space so the footer does not jump.
 *
 * @returns Placeholder block with the shared Loading copy.
 */
function PageFallback() {
  return (
    <div style={{ minHeight: '80vh', padding: 'clamp(56px,9vw,104px) clamp(20px,6vw,72px)', textAlign: 'center' }}>
      <p style={{ color: '#55524C' }}>Loading…</p>
    </div>
  );
}

/**
 * Root application shell for marketing routes and admin.
 *
 * @returns Routed app tree (reCAPTCHA only on captcha-gated routes).
 */
export default function App() {
  return <AppContent />;
}

/**
 * Route switch and homepage composition.
 *
 * @returns Current route UI including optional chat modal.
 */
function AppContent() {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatAudience, setChatAudience] = useState<'b2c' | 'builder'>('b2c');
  const openChat = (audience: 'b2c' | 'builder' = 'b2c') => {
    setChatAudience(audience);
    setChatOpen(true);
  };
  useScrollDepth();

  // Strip trailing slash so /estimator/ matches the same route as /estimator.
  // nginx can serve both URL forms; this prevents a hydration mismatch on the
  // client side when the URL has a trailing slash (GRI-296).
  const rawPathname = useRoute();
  const pathname = rawPathname.length > 1 && rawPathname.endsWith('/')
    ? rawPathname.slice(0, -1)
    : rawPathname;

  // Keep document.title in sync with the current route (reinforces the prerendered title).
  useEffect(() => {
    const titles: Record<string, string> = {
      '/estimator': 'Home Automation Cost Estimator | GrihaTEK',
      '/privacy-policy': 'Privacy Policy | GrihaTEK',
      '/terms-of-service': 'Terms of Service | GrihaTEK',
      '/guides': 'Smart Home Guides | GrihaTEK',
    };
    const t = titles[pathname];
    if (t) {
      document.title = t;
    } else if (pathname.startsWith('/guides/')) {
      const slug = pathname.slice('/guides/'.length);
      const guide = findGuide(slug);
      if (guide) document.title = `${guide.title} | GrihaTEK`;
    }
  }, [pathname]);

  if (pathname.startsWith('/admin')) {
    return (
      <Suspense fallback={<PageFallback />}>
        <AdminApp />
      </Suspense>
    );
  }

  if (pathname === '/privacy-policy') {
    return (
      <div className="app">
        <Header />
        <main>
          <Suspense fallback={<PageFallback />}>
            <PrivacyPolicy />
          </Suspense>
        </main>
        <Footer />
        <ConsentBanner />
        <MetaPixel />
      </div>
    );
  }

  if (pathname === '/terms-of-service') {
    return (
      <div className="app">
        <Header />
        <main>
          <Suspense fallback={<PageFallback />}>
            <TermsOfService />
          </Suspense>
        </main>
        <Footer />
        <ConsentBanner />
        <MetaPixel />
      </div>
    );
  }

  if (pathname === '/guides') {
    return (
      <div className="app">
        <Header />
        <main>
          <Suspense fallback={<PageFallback />}>
            <GuideIndex />
          </Suspense>
        </main>
        <Footer />
        <ConsentBanner />
        <MetaPixel />
      </div>
    );
  }

  if (pathname.startsWith('/guides/')) {
    const slug = pathname.slice('/guides/'.length);
    const guide = findGuide(slug);
    if (guide) {
      return (
        <div className="app">
          <Header />
          <main>
            <Suspense fallback={<PageFallback />}>
              <GuideArticle guide={guide} />
            </Suspense>
          </main>
          <Footer />
          <ConsentBanner />
          <MetaPixel />
        </div>
      );
    }
    // unknown slug falls through to NotFound
  }

  if (pathname === '/estimator') {
    return (
      <div className="app">
        <Header />
        <main>
          <Suspense fallback={<PageFallback />}>
            <RecaptchaProvider>
              <EstimatorWizard />
            </RecaptchaProvider>
          </Suspense>
        </main>
        <Footer />
        <ConsentBanner />
        <MetaPixel />
      </div>
    );
  }

  if (pathname !== '/') {
    return (
      <div className="app">
        <Header />
        <main>
          <Suspense fallback={<PageFallback />}>
            <NotFound />
          </Suspense>
        </main>
        <Footer />
        <ConsentBanner />
        <MetaPixel />
      </div>
    );
  }

  return (
    <div className="app">
      <SectionTracker sectionName="header">
        <Header />
      </SectionTracker>
      <main>
        <SectionTracker sectionName="hero">
          <Hero />
        </SectionTracker>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="fears">
            <Fears />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="rooms">
            <Rooms />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="roomguardian">
            <RoomGuardian />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="whathappens">
            <WhatHappens />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="builders">
            <Builders onOpenChat={() => openChat('builder')} />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="eldercare">
            <Eldercare />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="assistant">
            <Assistant setChatOpen={() => openChat('b2c')} />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="proof">
            <Proof />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="guides_teaser">
            <GuidesTeaser />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="faq">
            <FAQ />
          </SectionTracker>
        </Suspense>
        <Suspense fallback={<PageFallback />}>
          <SectionTracker sectionName="contact">
            <RecaptchaProvider>
              <Contact />
            </RecaptchaProvider>
          </SectionTracker>
        </Suspense>
      </main>
      <SectionTracker sectionName="footer">
        <Footer />
      </SectionTracker>
      {chatOpen && (
        <SectionTracker sectionName="chat">
          <Suspense fallback={null}>
            <ChatModal open={chatOpen} onClose={() => setChatOpen(false)} audience={chatAudience} />
          </Suspense>
        </SectionTracker>
      )}
      {!chatOpen && <ChatFAB onClick={() => openChat('b2c')} />}
      <ConsentBanner />
      <MetaPixel />
    </div>
  );
}
